import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_EMPLOYEE_PERMISSIONS,
  DEFAULT_OWNER_PERMISSIONS,
  PERMISSION_GROUPS,
  type PermissionCode,
} from '../../../shared/permissions';

type RoleCode = 'OWNER' | 'EMPLOYEE';

const ROLES: { code: RoleCode; labelKey: string; set: readonly PermissionCode[] }[] = [
  { code: 'OWNER', labelKey: 'users.role_owner', set: DEFAULT_OWNER_PERMISSIONS },
  { code: 'EMPLOYEE', labelKey: 'users.role_employee', set: DEFAULT_EMPLOYEE_PERMISSIONS },
];

const GROUP_LABEL_KEYS: Record<string, string> = {
  users: 'settings.permissions.group_users',
  master_data: 'settings.permissions.group_master_data',
  ledger: 'settings.permissions.group_ledger',
  trades: 'settings.permissions.group_trades',
  debts: 'settings.permissions.group_debts',
  reports: 'settings.permissions.group_reports',
};

// Read-only role × permission view. Editing happens per-user via the
// user form; this is a documentation surface — "what does each role
// see?" — for the owner.
export function PermissionMatrix() {
  const { t } = useTranslation();
  const [activeRole, setActiveRole] = useState<RoleCode>('OWNER');

  // ROLES is a static const — find() always returns a match for valid RoleCode values.
  const role = (ROLES.find((r) => r.code === activeRole) ?? ROLES[0]) as (typeof ROLES)[number];

  return (
    <>
      <p className="page-lede">{t('settings.permissions.readonly_note')}</p>

      {/* Role tab-strip */}
      <nav className="tabs" role="tablist" aria-label={t('settings.permissions.title')}>
        {ROLES.map((r) => (
          <button
            key={r.code}
            type="button"
            role="tab"
            aria-selected={activeRole === r.code}
            aria-current={activeRole === r.code ? 'page' : undefined}
            className="tabs__item"
            onClick={() => setActiveRole(r.code)}
          >
            {t(r.labelKey)}
          </button>
        ))}
      </nav>

      {/* Permission list for the active role */}
      <div role="tabpanel" aria-label={t(role.labelKey)}>
        {PERMISSION_GROUPS.map((group) => {
          const granted = group.codes.filter((c) => role.set.includes(c));
          const denied = group.codes.filter((c) => !role.set.includes(c));

          return (
            <div key={group.label} style={{ marginBlockEnd: 'var(--sp-4)' }}>
              <h3
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                  margin: '0 0 var(--sp-2)',
                }}
              >
                {t(GROUP_LABEL_KEYS[group.label] ?? group.label, {
                  defaultValue: group.label.replace(/_/g, ' '),
                })}
              </h3>

              <ul
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'grid',
                  gap: 'var(--sp-1)',
                }}
              >
                {granted.map((code) => (
                  <li
                    key={code}
                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}
                  >
                    <span
                      aria-hidden="true"
                      style={{ color: 'var(--in-text, #0a7c42)', fontWeight: 700, width: 16 }}
                    >
                      ✓
                    </span>
                    <code style={{ fontSize: 13 }}>{code}</code>
                  </li>
                ))}
                {denied.map((code) => (
                  <li
                    key={code}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                      opacity: 0.4,
                    }}
                  >
                    <span aria-hidden="true" style={{ width: 16, textAlign: 'center' }}>
                      ·
                    </span>
                    <code style={{ fontSize: 13 }}>{code}</code>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </>
  );
}
